/* eslint-disable no-console */
import { useState, useEffect, useMemo } from 'react';
import Flex from '@deprecated-ui/common/Flex';
import Text from '@deprecated-ui/common/Text';
import Button from '@deprecated-ui/common/Button';
import color from '@deprecated-ui/_style/color';
import shallow from 'zustand/shallow';
import { useTradingInfoStore } from 'global/Securities/stores/useTradingInfoStore';
import { lotSize } from 'global/TradingModal/constant';
import {
  clevertapOrderCanceled,
  clevertapOrderConfirmed,
  clevertapOrderPreviewed,
} from 'lib/analytics/securities';
import {
  trackAutoBuyConfirmOrderCanceled,
  trackAutoBuyOrderConfirmed,
  trackAutoBuyPreviewOrderView,
} from 'lib/analytics/autoOrder';
import { generateOrderTypeLabel } from 'utils/securities/analytics';
import {
  BUY_ORDER_TYPE_TEXT,
  CorporateActionStockType,
  ORDER_TYPE,
} from 'constants/trading';
import { VIRTUAL_TRADING_TYPES } from 'lib/analytics/virtualTrading';
import useTradingAccount from 'hooks/useTradingAccount';
import If from 'global/ErrorHandlers/If';
import tradingOperationStore from 'lib/stores/tradingOperation';
import tradingDataStore from 'lib/stores/tradingData';
import tradingModalStore from 'global/TradingModal/store';
import { PreviewModalContainer } from 'global/TradingModal/styled';
import {
  expiryId,
  expiryOptions,
} from 'global/Securities/constants/transactionForm';
import useShareTradeShare from 'stores/sharetrade/useShareTradeShare';
import { ShareOrderType, ShareOrderStatus } from 'typings/api/shareTradeShare';
import {
  BuyOrderType,
  SellOrderType,
  SmartOrderCode,
  PaymentMethod,
  Expiry,
  PlatformOrderTypes,
} from 'global/Securities/types/transaction';
import useUIConfigStore from 'stores/useUIConfigStore';
import useShareTradeModal from 'features/sharetrade/stores/useShareTradeModal';
import { formatHourMinutes } from 'features/day-trade/utils/formater';
import useGetPortfolioData from 'hooks/useGetPortfolioData';
import useTradingStore from 'features/transaction/useTradingStore';
import SellPreview from './SellPreview';
import BuyPreview from './BuyPreview';
import ExercisePreview from './ExercisePreview';
import ProfitLoss from './ProfitLoss';
import realTradingStore from '../store';
import { getSplitOrderPayload } from '../utils';
import { DayTradeInfoBanner } from '../styled';
import { defaultPreviewModalTitle, previewModalTitle } from './constants';

const expiryOption: Expiry[] = [Expiry.GoodForDay, Expiry.GoodTillCancelled];

interface PreviewModalProps {
  visible: boolean;
  showExchangeFee: () => void;
  onCancel: () => void;
  onSubmit: () => void;
}

function PreviewOrderModal({
  visible,
  showExchangeFee,
  onCancel,
  onSubmit,
}: PreviewModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const previewData = useTradingStore((s) => s.previewData);

  const portfolioData = useGetPortfolioData(
    previewData.symbol,
    true,
    false,
    previewData.isDayTrade,
  );

  const { orderid, modalData, setModalState } = tradingModalStore(
    (s) => ({
      orderid: s.orderid,
      modalData: s.modalData,
      setModalState: s.setModalState,
    }),
    shallow,
  );
  const { exerciseData, getExercise, productType } = tradingDataStore(
    (s) => ({
      exerciseData: s.exercise?.data,
      getExercise: s.getExercise,
      productType: s.company.data?.type_company,
    }),
    shallow,
  );
  const {
    postTradeBuy,
    postDayTradeBuy,
    postSmartOrder,
    postTradeAmend,
    postDayTradeAmend,
    postTradeSell,
    postRequestExercise,
    editSmartOrder,
    postDayTradeSell,
  } = tradingOperationStore();
  const { isRealTrading } = useTradingAccount();
  const setUIValue = useUIConfigStore((s) => s.setUIValue);

  const {
    price,
    shares,
    triggerPrice,
    gtc,
    symbol,
    orderType,
    isSplitOrderActive,
    splitQuantity,
    splitRangeMin,
    splitRangeMax,
    multiplier,
    configType,
  } = previewData;

  const type =
    previewData.type === 'Auto Buy' ? BuyOrderType.AutoBuy : previewData.type;
  const expiry = expiryOption[gtc];
  const dayTradeMultiplier = Number(multiplier);

  const isMarket = type === BuyOrderType.MarketOrder;
  const isLimit = type === BuyOrderType.LimitOrder;
  const isDayTrade = previewData.isDayTrade && isLimit;
  const platform_order_type = () => {
    if (isMarket) return PlatformOrderTypes.FAK;
    if (isLimit) return PlatformOrderTypes.LIMIT;
    return PlatformOrderTypes.UNSPECIFIED;
  };

  const {
    stockCode,
    resetOrderData,
    splitMethod,
    shares: storeShares,
  } = realTradingStore();

  const [setShareOrderType, setShareExpiry, setIsDayTrade] = useShareTradeModal(
    (s) => [s.setOrderType, s.setExpiry, s.setIsDayTrade],
    shallow,
  );

  const setShareTransactionData = useShareTradeShare(
    (s) => s.setTransactionData,
  );

  const { dayTrade } = useTradingInfoStore((s) => ({
    dayTrade: s.dayTrade,
  }));

  const isCorporateAction = useMemo(
    () => CorporateActionStockType.includes(productType?.toLowerCase()),
    [productType],
  );

  const lot = shares / lotSize;

  const isBuy =
    orderType === ORDER_TYPE.BUY || orderType === ORDER_TYPE.AMEND_BUY;
  const isSell =
    orderType === ORDER_TYPE.SELL || orderType === ORDER_TYPE.AMEND_SELL;
  const isExercise = orderType === ORDER_TYPE.EXERCISE;

  const onClose = () => {
    trackAutoBuyConfirmOrderCanceled(
      stockCode,
      BUY_ORDER_TYPE_TEXT[type],
      expiryOptions[expiry],
      triggerPrice,
      price,
      shares,
    );
    onCancel();
  };

  const handleSetTransactionData = (orderId: string, isBuyOrder: boolean) => {
    const shareOrderType = isBuyOrder
      ? ShareOrderType.Buy
      : ShareOrderType.Sell;

    setShareTransactionData({
      order_id: orderId,
      price,
      status: ShareOrderStatus.Open,
      symbol,
      type: shareOrderType,
      order_time: new Date().toISOString(),
      qty: shares,
      total_investment: price * shares,
    });
  };

  const submitOrderBuy = async () => {
    // Set default payment method to Trading Balance
    setUIValue('transactionPaymentMethod', PaymentMethod.TradingBalance);

    if (isDayTrade) {
      const dayTradeData = {
        symbol,
        price,
        shares: (lot * lotSize).toString(),
        multiplier: dayTradeMultiplier,
      };

      const result = await postDayTradeBuy(dayTradeData);

      handleSetTransactionData(result.data?.order_id, true);

      return Boolean(result && !result.error);
    }

    const result = await postTradeBuy({
      accountType: 'stockbit',
      orderkey: `W-BUY-${Date.now()}`,
      symbol,
      price,
      shares: lot * 100,
      boardtype: 'RG',
      gtc: expiry,
      platform_order_type: platform_order_type(),
      ...getSplitOrderPayload({
        splitOrderActive: isSplitOrderActive,
        splitQuantity,
        splitRangeMin,
        splitRangeMax,
        splitMethod,
      }),
    });
    handleSetTransactionData(result?.data?.order_id, true);
    return Boolean(result && !result.error);
  };

  const submitSmartOrder = async () => {
    const result = await postSmartOrder({
      config_type: configType?.toString(),
      shares: shares?.toString(),
      gtc: `${expiryId[expiry]}`,
      trigger_price: triggerPrice,
      stock_code: stockCode,
      price,
      type: 'AB' as SmartOrderCode,
    });
    return Boolean(result && !result.error);
  };

  const submitAmend = async () => {
    if (isDayTrade) {
      const data = {
        orderid,
        price,
        shares: (lot * lotSize).toString(),
        multiplier: dayTradeMultiplier,
      };

      const result = await postDayTradeAmend(data);
      return Boolean(result && !result.error);
    }

    const data = {
      price,
      shares: lot * lotSize,
      order_id: orderid,
    };
    const result = await postTradeAmend([data]);
    return Boolean(result && !result.error);
  };

  const editAutoBuy = async () => {
    const result = await editSmartOrder({
      id: orderid,
      trigger_price: triggerPrice,
      shares: (lot * lotSize)?.toString(),
      config_type: configType?.toString(),
      price,
    });
    return Boolean(result && !result.error);
  };

  const submitOrderSell = async () => {
    if (isDayTrade) {
      const result = await postDayTradeSell({
        symbol,
        price,
        shares: (lot * lotSize).toString(),
      });

      handleSetTransactionData(result.data?.order_id, false);

      return Boolean(result && !result.error);
    }

    const data = {
      accountType: 'stockbit',
      orderkey: `W-SELL-${Date.now()}`,
      symbol,
      price,
      shares: lot * 100,
      boardtype: 'RG',
      gtc: expiry,
      platform_order_type: platform_order_type(),
      ...getSplitOrderPayload({
        splitOrderActive: isSplitOrderActive,
        splitQuantity,
        splitRangeMin,
        splitRangeMax,
        splitMethod,
      }),
    };

    const result = await postTradeSell(data);

    handleSetTransactionData(result.data?.order_id, false);

    return Boolean(result && !result.error);
  };

  const submitRequestExercise = async () => {
    const data = {
      orderKey: `W-EXERCISE-${Date.now()}`,
      stockCode,
      shares: storeShares,
    };
    const result = await postRequestExercise(data);
    return Boolean(result && !result.error);
  };

  const handleAPIResponse = async (apiCall: () => Promise<boolean>) => {
    setIsLoading(true);
    try {
      const result = await apiCall();
      if (result) {
        onSubmit();
        resetOrderData();
      } else onCancel();
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBuyOrderRequest = () => {
    const buyOrderRequest = {
      [BuyOrderType.LimitOrder]: submitOrderBuy,
      [BuyOrderType.MarketOrder]: submitOrderBuy,
      [BuyOrderType.AutoBuy]: submitSmartOrder,
    };

    setShareExpiry(expiry);
    setShareOrderType(type as BuyOrderType | SellOrderType);

    setIsDayTrade(isDayTrade);

    handleAPIResponse(buyOrderRequest[type] || submitOrderBuy);
  };

  const handleSellOrderRequest = () => {
    const sellOrderRequest = {
      [SellOrderType.LimitOrder]: submitOrderSell,
      [SellOrderType.MarketOrder]: submitOrderSell,
      [SellOrderType.StopLoss]: submitSmartOrder,
      [SellOrderType.TakeProfit]: submitSmartOrder,
    };

    setShareExpiry(expiry);
    setShareOrderType(type as BuyOrderType | SellOrderType);

    setIsDayTrade(isDayTrade);

    handleAPIResponse(sellOrderRequest[type] || submitOrderSell);
  };

  const onConfirm = async () => {
    if (isLoading) return;

    trackAutoBuyOrderConfirmed(
      stockCode,
      BUY_ORDER_TYPE_TEXT[type],
      expiryOptions[expiry],
      triggerPrice,
      price,
      shares,
    );
    const isAmend =
      orderType === ORDER_TYPE.AMEND_BUY || orderType === ORDER_TYPE.AMEND_SELL;

    if (isAmend && type === BuyOrderType.AutoBuy) {
      handleAPIResponse(editAutoBuy);
    } else if (isAmend) {
      handleAPIResponse(submitAmend);
    }

    if (orderType === ORDER_TYPE.BUY) {
      handleBuyOrderRequest();
    }

    if (orderType === ORDER_TYPE.SELL) {
      handleSellOrderRequest();
    }

    if (orderType === ORDER_TYPE.EXERCISE) {
      handleAPIResponse(submitRequestExercise);
    }
  };

  const handleConfirm = () => {
    if (isRealTrading) {
      clevertapOrderConfirmed({
        symbol,
        price,
        shares,
        orderType,
        type: VIRTUAL_TRADING_TYPES.realTrading,
        expiry: expiryOptions[expiry],
        orderTypeStatus: generateOrderTypeLabel(type),
        splitOrder: isSplitOrderActive,
        dayTrade: isDayTrade,
      });
    }
    onConfirm();

    if (modalData?.openFromWidget) {
      setModalState({
        modalData: {
          ...modalData,
          openFromWidget: false,
        },
      });
    }
  };

  const loadExercisableData = async (): Promise<void> => {
    try {
      await getExercise(symbol);
    } catch (e) {
      onClose();
    }
  };

  useEffect(() => {
    if (visible) {
      clevertapOrderPreviewed({
        symbol,
        orderType,
      });

      trackAutoBuyPreviewOrderView();

      if (orderType === ORDER_TYPE.EXERCISE) loadExercisableData();
    }
  }, [symbol, visible]);

  let isNeedToRenderInfo = false;

  if (exerciseData?.time_server) {
    const timeServer = new Date(exerciseData?.time_server);
    const secondsInDay =
      timeServer.getSeconds() +
      timeServer.getMinutes() * 60 +
      timeServer.getHours() * 3600;
    const limitCutOff = 10 * 3600; // 10:00 AM
    isNeedToRenderInfo = secondsInDay > limitCutOff;
  }

  const closeHour = formatHourMinutes(
    dayTrade?.trading_open,
    dayTrade?.trading_close,
  ).close;

  return (
    <PreviewModalContainer
      visible={visible}
      closable={false}
      centered
      width="100%"
      style={{ maxWidth: 480 }}
    >
      <Flex
        width="100%"
        direction="column"
        align="center"
        padding="40px 20px"
        style={{ gap: 16 }}
      >
        <Text family="bold" size="18px" height="24px">
          {previewModalTitle[orderType] || defaultPreviewModalTitle}
        </Text>

        <Flex
          align="center"
          justify="center"
          padding="16px 20px"
          style={{
            border: '1px solid #dadada',
            borderRadius: '5px',
            width: '100%',
          }}
        >
          {isBuy && (
            <BuyPreview showExchangeFee={showExchangeFee} data={previewData} />
          )}
          {isSell && (
            <SellPreview showExchangeFee={showExchangeFee} data={previewData} />
          )}
          {isExercise && <ExercisePreview />}
        </Flex>

        <If condition={isBuy && isDayTrade}>
          <DayTradeInfoBanner>
            <Text>
              Saham yang dibeli dengan Day Trade akan terjual otomatis hari ini
              pukul {closeHour} WIB.
            </Text>
          </DayTradeInfoBanner>
        </If>

        <If condition={isExercise && isNeedToRenderInfo}>
          {/* @ts-expect-error */}
          <Text color={color.highgray} align="center" mt="15px">
            <span className="exercise-information">
              Penebusan ini akan diproses <br /> di hari bursa berikutnya karena
              sudah melewati cut-off kami pukul 10:00 WIB.
            </span>
          </Text>
        </If>

        <If condition={isSell && !isMarket}>
          <ProfitLoss
            priceAverageFee={portfolioData?.price_average_fee}
            isCorporateAction={isCorporateAction}
          />
        </If>
      </Flex>

      <Flex
        justify="space-between"
        style={{
          position: 'absolute',
          bottom: '-60px', // 44px (Button height) + 16px (Gap)
          width: '100%',
        }}
      >
        <Button
          onClick={() => {
            setIsLoading(false);
            if (isRealTrading) {
              clevertapOrderCanceled({
                symbol,
                price,
                shares,
                orderType,
                type: VIRTUAL_TRADING_TYPES.realTrading,
              });
            }
            onClose();
          }}
          type="secondary"
          radius="4px"
          block
          height="44px"
          style={{ width: '49%' }}
        >
          <Text
            align="inherit"
            color="inherit"
            family="bold"
            size="14px"
            height="19px"
          >
            Cancel
          </Text>
        </Button>

        <Button
          onClick={handleConfirm}
          loading={isLoading}
          radius="4px"
          block
          height="44px"
          style={{ width: '49%' }}
        >
          <Text
            align="inherit"
            color={color.primary}
            family="bold"
            size="14px"
            height="19px"
          >
            Confirm
          </Text>
        </Button>
      </Flex>
    </PreviewModalContainer>
  );
}

export default PreviewOrderModal;
